/**
 * @fileoverview Approval gate page shown to users whose accounts are pending
 * or rejected by an admin. Prevents access to the main application.
 */

import { useAuth0 } from '@auth0/auth0-react';
import styles from './PendingApprovalPage.module.css';

/**
 * PendingApprovalPage Component
 *
 * Displayed to users who have signed up but are awaiting admin approval.
 * Shows different messages for pending vs rejected users.
 *
 * @param {Object} props
 * @param {boolean} props.isRejected - Whether the user was rejected (vs pending)
 * @param {string} props.userEmail - User's email address
 */
function PendingApprovalPage({ isRejected, userEmail }) {
    const { logout } = useAuth0();

    const handleLogout = () => {
        logout({ logoutParams: { returnTo: window.location.origin } });
    };

    if (isRejected) {
        return (
            <div className={styles.container}>
                <div className={styles.card}>
                    <div className={styles.iconRejected}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="15" y1="9" x2="9" y2="15" />
                            <line x1="9" y1="9" x2="15" y2="15" />
                        </svg>
                    </div>
                    <h1 className={styles.title}>Access Denied</h1>
                    <p className={styles.message}>
                        Unfortunately, your request to access Compass Ledger has been declined.
                    </p>
                    <p className={styles.email}>{userEmail}</p>
                    <p className={styles.submessage}>
                        If you believe this is a mistake, please contact the administrator.
                    </p>
                    <button onClick={handleLogout} className={styles.logoutButton}>
                        Sign Out
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.card}>
                <div className={styles.iconPending}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                    </svg>
                </div>
                <h1 className={styles.title}>Awaiting Approval</h1>
                <p className={styles.message}>
                    Thank you for signing up for Compass Ledger!
                </p>
                <p className={styles.email}>{userEmail}</p>
                <p className={styles.submessage}>
                    Your account is pending approval from an administrator.
                    You will be able to access the application once your account has been approved.
                </p>
                <div className={styles.actions}>
                    <button
                        onClick={() => window.location.reload()}
                        className={styles.refreshButton}
                    >
                        Check Status
                    </button>
                    <button onClick={handleLogout} className={styles.logoutButton}>
                        Sign Out
                    </button>
                </div>
            </div>
        </div>
    );
}

export default PendingApprovalPage;
